import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Grid, Link, Typography, makeStyles } from '@material-ui/core'
import { Edit as EditIcon } from '@material-ui/icons'
import { sortBy } from 'lodash'
import slugify from 'slugify'
import { createMarkup } from 'civic-champs-shared/core/utils/helpers'
import CopyButton from 'civic-champs-shared/core/copy-button'
import Loading from 'components/Loading'
import FeatureEnabled from 'core/feature/components/FeatureEnabled'
import { useCurrentOrg } from 'auth/hooks'
import { useFeatureEnabled } from 'core/feature/hooks'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import { GroupTypeCode, PermissionOptions } from 'Event/components/opportunity/GroupPicker/types'
import { useGroupCredentials } from 'group/hooks/useGroupCredentials'
import { useFetchGroupQuestionSets } from 'group/hooks/useFetchGroupQuestionSets'
import EditGroupOverviewDialog from 'group/components/EditGroupOverviewDialog'
import { Person } from 'civic-champs-shared/common/types'
import EditGroupApplicationDialog from 'group/components/EditGroupApplicationDialog'
import EditGroupSettingsDialog from 'group/components/EditGroupSettingsDialog'
import useOrganizationCredentials from 'credential/hooks/useOrganizationCredentials'

const useStyles = makeStyles(() => ({
  section: {
    padding: '24px 0',
    borderBottom: '1px solid #E0E3E3',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 700,
    lineHeight: '24px',
    letterSpacing: '0.1px',
    margin: 0,
  },
  editIcon: {
    cursor: 'pointer',
    color: '#0F5DB5',
    width: '20px',
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '12px',
    gap: '8px',
  },
  fieldRowCenter: {
    alignItems: 'center',
  },
  fieldLabel: {
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
    minWidth: '180px',
    flexShrink: 0,
    margin: 0,
  },
  fieldValue: {
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: '#000',
    maxWidth: '720px',
    '& > :first-child': {
      marginTop: 0,
    },
    '& p:first-child': {
      marginTop: 0,
    },
  },
  emptyValue: {
    fontStyle: 'italic',
    color: '#747778',
  },
  applicationLink: {
    color: '#3776CF',
    fontWeight: 600,
    wordBreak: 'break-all',
  },
  disabledLink: {
    color: '#747778',
  },
  statusValue: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px',
    lineHeight: '20px',
    minHeight: '20px',
  },
  copyButtonWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    marginLeft: '4px',
    flexShrink: 0,
    overflow: 'visible',
  },
  copyButton: {
    margin: '-6px !important',
    padding: '6px !important',
    width: '32px !important',
    height: '32px !important',
    '& .copy-button__icon': {
      width: '18px',
      height: '18px',
      margin: 0,
      backgroundSize: 'contain',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '18px',
    },
  },
  formsList: {
    margin: 0,
    paddingLeft: '20px',
  },
}))

interface GroupManageTabProps {
  group: any
  onGroupUpdate?: (payload: Record<string, unknown>) => Promise<unknown>
}

const ReadOnlyField = ({
  label,
  children,
  centerAlign,
}: {
  label: string
  children: React.ReactNode
  centerAlign?: boolean
}) => {
  const classes = useStyles()
  return (
    <div className={`${classes.fieldRow} ${centerAlign ? classes.fieldRowCenter : ''}`}>
      <Typography component="span" className={classes.fieldLabel}>
        {label}:
      </Typography>
      <div className={classes.fieldValue}>{children}</div>
    </div>
  )
}

const SectionHeader = ({ title, onEdit }: { title: string; onEdit?: () => void }) => {
  const classes = useStyles()
  return (
    <div className={classes.sectionHeader}>
      <div className={classes.sectionTitle}>{title}</div>
      {onEdit && <EditIcon className={classes.editIcon} onClick={onEdit} />}
    </div>
  )
}

export default function GroupManageTab({ group, onGroupUpdate }: GroupManageTabProps) {
  const classes = useStyles()
  const currentOrg = useCurrentOrg()
  const isAutomaticEventHourLoggingEnabled = useFeatureEnabled('AutomaticEventHourLogging')
  const isVolunteerApplicationEnabled = useFeatureEnabled('VolunteerApplication')
  const showEditOverviewDialog = useShowPrompt(EditGroupOverviewDialog)
  const showEditSettingsDialog = useShowPrompt(EditGroupSettingsDialog)
  const showEditApplicationDialog = useShowPrompt(EditGroupApplicationDialog)
  const [credentials, setCredentials] = useState<any[]>([])
  const [questionSets, setQuestionSets] = useState<any[]>([])
  const { loading: credentialsLoading, reFetch: reFetchCredentials } = useGroupCredentials(group.id, setCredentials)
  const [fetchGroupQuestionSets, questionSetsRequest] = useFetchGroupQuestionSets(setQuestionSets) as [
    (groupId: number) => Promise<any>,
    { loading?: boolean },
  ]
  const { credentials: organizationCredentials } = useOrganizationCredentials()
  const questionSetsLoading = questionSetsRequest?.loading

  useEffect(() => {
    fetchGroupQuestionSets(group.id)
  }, [fetchGroupQuestionSets, group.id])

  const isVolunteersGroup = group.groupType?.code === GroupTypeCode.Volunteer
  const canEditOverview = group.groupType?.code === GroupTypeCode.UserDefined || isVolunteersGroup
  const canEditApplication = !!onGroupUpdate
  const canChangeMembership = group.permissionOptions === PermissionOptions.EDITABLE
  const canEditSettings = !!onGroupUpdate && (isAutomaticEventHourLoggingEnabled || isVolunteerApplicationEnabled)
  const canChangeApplyGroup = ![PermissionOptions.PERMANENTLY_CLOSED, PermissionOptions.PERMANENTLY_OPEN].includes(
    group.permissionOptions,
  )
  const canChangeStatus = canChangeApplyGroup && isVolunteerApplicationEnabled

  const handleEditOverview = useCallback(async () => {
    if (!onGroupUpdate) {
      return
    }

    await showEditOverviewDialog({
      group,
      onSubmit: onGroupUpdate,
      canChangeMembership,
      canChangeStatus,
      nameReadOnly: isVolunteersGroup,
    })
  }, [canChangeMembership, canChangeStatus, group, isVolunteersGroup, onGroupUpdate, showEditOverviewDialog])

  const handleEditSettings = useCallback(async () => {
    if (!onGroupUpdate) {
      return
    }

    await showEditSettingsDialog({
      group,
      onSubmit: onGroupUpdate,
    })
  }, [group, onGroupUpdate, showEditSettingsDialog])

  const handleEditApplication = useCallback(async () => {
    if (!onGroupUpdate) {
      return
    }

    await showEditApplicationDialog({
      group,
      credentials,
      questionSets,
      onSubmitGroup: onGroupUpdate,
      onCredentialsSaved: setCredentials,
      onQuestionSetsSaved: setQuestionSets,
    })

    reFetchCredentials()
    fetchGroupQuestionSets(group.id)
  }, [
    credentials,
    fetchGroupQuestionSets,
    group,
    onGroupUpdate,
    questionSets,
    reFetchCredentials,
    showEditApplicationDialog,
  ])

  const notificationRecipientNames = useMemo(() => {
    const recipients = group.applicationNotificationPersons || []
    if (!recipients.length) {
      return null
    }

    return recipients.map(({ givenName, familyName }: Person) => `${givenName} ${familyName}`.trim()).join(', ')
  }, [group.applicationNotificationPersons])

  const applicationLink = `${process.env.REACT_APP_ONBOARDING_SITE_ENDPOINT}/apply/${slugify(
    currentOrg.name,
  ).toLowerCase()}/${group.id}`

  const credentialTitleById = useMemo(
    () =>
      organizationCredentials.reduce<Record<number, string>>((titles, credential) => {
        titles[credential.credential.id] = credential.version.title
        return titles
      }, {}),
    [organizationCredentials],
  )

  const forms = useMemo(() => {
    const credentialForms = credentials
      .filter(item => ['waiver', 'identification'].includes(item.type))
      .map(item => ({
        key: `${item.type}-${item.id}`,
        name: credentialTitleById[item.id] || item.name,
        displayOrder: item.displayOrder ?? 0,
        required: item.required ?? false,
      }))
    const questionnaireForms = questionSets.map(item => ({
      key: `questionnaire-${item.questionSet?.id ?? item.survey?.id}`,
      name: item.questionSet?.name,
      displayOrder: item.displayOrder ?? 0,
      required: item.required ?? false,
    }))

    return sortBy([...credentialForms, ...questionnaireForms], 'displayOrder').filter(item => item.name)
  }, [credentialTitleById, credentials, questionSets])

  if (credentialsLoading || questionSetsLoading) {
    return <Loading />
  }

  return (
    <Grid container direction="column">
      <Grid item className={classes.section}>
        <SectionHeader title="Overview" onEdit={canEditOverview && onGroupUpdate ? handleEditOverview : undefined} />
        <ReadOnlyField label="Name">{group.name}</ReadOnlyField>
        <ReadOnlyField label="Description">
          {group.description ? (
            <div dangerouslySetInnerHTML={createMarkup(group.description)} />
          ) : (
            <span className={classes.emptyValue}>None</span>
          )}
        </ReadOnlyField>
        <ReadOnlyField label="Group Membership">{group.closed ? 'Requires Approval' : 'Open Membership'}</ReadOnlyField>
        {canChangeApplyGroup && (
          <FeatureEnabled code="VolunteerApplication">
            <ReadOnlyField label="Status" centerAlign>
              {group.useApplicationLink ? 'Accepting Applications' : 'Not Accepting Applications'}
            </ReadOnlyField>
          </FeatureEnabled>
        )}
      </Grid>

      <Grid item className={classes.section}>
        <SectionHeader title="Application" onEdit={canEditApplication ? handleEditApplication : undefined} />
        {canChangeApplyGroup && (
          <FeatureEnabled code="VolunteerApplication">
            <ReadOnlyField label="Link" centerAlign>
              <span className={classes.statusValue}>
                {group.useApplicationLink ? (
                  <Link href={applicationLink} target="_blank" className={classes.applicationLink}>
                    {applicationLink}
                  </Link>
                ) : (
                  <span className={classes.disabledLink}>{applicationLink}</span>
                )}
                <span className={classes.copyButtonWrap}>
                  <CopyButton
                    className={classes.copyButton}
                    onClick={() => navigator.clipboard.writeText(applicationLink)}
                  />
                </span>
              </span>
            </ReadOnlyField>
          </FeatureEnabled>
        )}
        <ReadOnlyField label="Starting Instructions">
          {group.applicationStartInstructions ? (
            <div dangerouslySetInnerHTML={createMarkup(group.applicationStartInstructions)} />
          ) : (
            <span className={classes.emptyValue}>Default instructions</span>
          )}
        </ReadOnlyField>
        <ReadOnlyField label="Forms">
          {forms.length > 0 ? (
            <ol className={classes.formsList}>
              {forms.map(form => (
                <li key={form.key}>
                  {form.name}
                  {form.required ? ' *' : ''}
                </li>
              ))}
            </ol>
          ) : (
            <span className={classes.emptyValue}>None</span>
          )}
        </ReadOnlyField>
        <ReadOnlyField label="Completion Instructions">
          {group.applicationCompletionInstructions ? (
            <div dangerouslySetInnerHTML={createMarkup(group.applicationCompletionInstructions)} />
          ) : (
            <span className={classes.emptyValue}>Default instructions</span>
          )}
        </ReadOnlyField>
      </Grid>

      <Grid item className={classes.section}>
        <SectionHeader title="Settings" onEdit={canEditSettings ? handleEditSettings : undefined} />
        {isAutomaticEventHourLoggingEnabled && (
          <ReadOnlyField label="Automatic Check-In">
            {group.automaticHourLogging
              ? 'Members of this group automatically record their hours for their registered events'
              : 'Members of this group are required to check-in for their activities'}
          </ReadOnlyField>
        )}
        {isVolunteerApplicationEnabled && (
          <ReadOnlyField label="Email Notifications">
            {notificationRecipientNames || <span className={classes.emptyValue}>None</span>}
          </ReadOnlyField>
        )}
      </Grid>
    </Grid>
  )
}
